import React from 'react'
import { motion } from 'framer-motion'
import { Award, Zap, ShieldCheck, HeadphonesIcon, ArrowRight } from 'lucide-react'
import { useNavigate, Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

const WhyUsPage = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const values = [
        {
            icon: <Award className="w-7 h-7" />,
            title: t('whyUs.cards.expertise.title'),
            desc: t('whyUs.cards.expertise.desc'),
            color: 'bg-primary text-white',
        },
        {
            icon: <Zap className="w-7 h-7" />,
            title: t('whyUs.cards.integration.title'),
            desc: t('whyUs.cards.integration.desc'),
            color: 'bg-brand-teal text-white',
        },
        {
            icon: <ShieldCheck className="w-7 h-7" />,
            title: t('whyUs.cards.quality.title'),
            desc: t('whyUs.cards.quality.desc'),
            color: 'bg-brand-accent text-white',
        },
        {
            icon: <HeadphonesIcon className="w-7 h-7" />,
            title: t('whyUs.cards.support.title'),
            desc: t('whyUs.cards.support.desc'),
            color: 'bg-brand-primary-dark text-white',
        },
    ]

    return (
        <div className="pt-24 sm:pt-32 pb-24 px-4 sm:px-6 overflow-x-hidden">
            <div className="max-w-5xl mx-auto">
                <nav className="flex items-center gap-1.5 text-xs mb-8 flex-wrap">
                    <Link to="/" className="text-text-dim hover:text-primary transition-colors">{t('header.home')}</Link>
                    <ChevronRight className="w-3 h-3 text-text-dim/40" />
                    <span className="text-text-main font-medium">{t('header.whyUs', 'Why Us')}</span>
                </nav>
                <motion.div className="text-center mb-16 sm:mb-20" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                    <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-tight text-text-main">
                        {t('whyUs.title.part1')}
                        <br />
                        <span className="text-gradient-green">{t('whyUs.title.part2')}</span>
                    </h1>
                </motion.div>

                <div className="space-y-6">
                    {values.map((val, i) => (
                        <motion.div
                            key={i}
                            className="bio-card p-6 sm:p-8 md:p-10 group"
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                        >
                            <div className="flex flex-col sm:flex-row gap-6">
                                <div className={`w-14 h-14 rounded-xl ${val.color} flex items-center justify-center flex-shrink-0`}>
                                    {val.icon}
                                </div>
                                <div>
                                    <h3 className="text-xl font-display font-bold mb-3 text-text-main group-hover:text-primary transition-colors">{val.title}</h3>
                                    <p className="text-text-muted text-sm leading-relaxed">{val.desc}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    className="mt-16"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                >
                    <div className="bio-card-tinted rounded-2xl p-6 sm:p-8 border border-primary/20 flex flex-col sm:flex-row items-center gap-6 text-center sm:text-left">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white shadow-soft flex items-center justify-center text-primary flex-shrink-0">
                            <span className="material-symbols-outlined text-3xl">contact_support</span>
                        </div>
                        <div className="flex-1 min-w-0">
                            <h3 className="font-display font-bold text-text-main break-words mb-4">{t('whyUs.ctaSection.title')}</h3>
                            <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                                <a href="/contact" className="btn-primary px-5 py-2 text-xs flex items-center gap-2">{t('whyUs.ctaSection.contactBtn')} <span className="material-symbols-outlined text-sm">arrow_forward</span></a>
                                <a href="/service-process" className="px-5 py-2 text-xs font-bold text-text-muted hover:text-primary transition-colors flex items-center gap-1.5">{t('whyUs.ctaSection.processBtn')} <span className="material-symbols-outlined text-sm">arrow_forward</span></a>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    )
}

export default WhyUsPage
